import { useMemo } from 'react';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import Typography from '@mui/material/Typography';
import BreadCrumb from 'shared/components/bread-crumb/bread-crumb/bread-crumb';
import OrganisationTreeNode from 'shared/features/organisation/models/organisation-tree-node';

type Props = {
  rootNodeId?: string;
  upstreamParentBranch: OrganisationTreeNode[];
  showIcon?: boolean;
  maxVisibleBreadCrumbs?: number;
  textStyle?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'subtitle1'
    | 'subtitle2'
    | 'body1'
    | 'body2'
    | 'caption'
    | 'button'
    | 'overline'
    | 'inherit';
};

const OrganisationBreadCrumb = ({
  rootNodeId,
  upstreamParentBranch,
  showIcon,
  maxVisibleBreadCrumbs,
  textStyle,
}: Readonly<Props>): React.JSX.Element => {
  const fillBreadCrumbs = (breadCrumbs: string[], parentId?: string): string[] => {
    const parent = upstreamParentBranch.find((node) => node.id === parentId);

    if (parent) {
      breadCrumbs.unshift(parent.name);
      return fillBreadCrumbs(breadCrumbs, parent.parentId);
    }

    return breadCrumbs;
  };

  const breadCrumbs = useMemo(() => fillBreadCrumbs([], rootNodeId), [upstreamParentBranch, rootNodeId]);
  const startIcon = showIcon ? <PinDropOutlinedIcon /> : undefined;

  return (
    <BreadCrumb
      showEllipsis={breadCrumbs.length > (maxVisibleBreadCrumbs ?? 4)}
      startIcon={startIcon}
      allElementNames={breadCrumbs}
      data-testid="organisationBreadCrumb"
    >
      {breadCrumbs.map((parentTitle: string, index: number) => (
        <Typography data-testid={`breadcrumb-child-${index}`} key={`Breadcrumb_Child_${parentTitle}`} variant={textStyle ?? 'subtitle1'}>
          {parentTitle}
        </Typography>
      ))}
    </BreadCrumb>
  );
};

export default OrganisationBreadCrumb;
